import moment from 'moment'
import {
  faArrowDown,
  faCalendarCheck,
  faClipboardList,
  faCoins,
  faFire,
  faGift,
  faRocket,
  faStore,
  faTicketAlt,
  faUndo,
  faUserPlus
} from '@fortawesome/free-solid-svg-icons'

// Colores de insignia por nivel, según `badge_icon` de la API (bronze, silver,
// gold). Van fijos porque son la identidad de cada nivel, no colores del tema.
export const LEVEL_STYLES = {
  bronze: { color: '#b0702c', background: 'rgba(176, 112, 44, 0.12)' },
  silver: { color: '#6c7a89', background: 'rgba(108, 122, 137, 0.14)' },
  gold: { color: '#c99a06', background: 'rgba(201, 154, 6, 0.14)' }
}

export const getLevelStyle = (badgeIcon) => LEVEL_STYLES[badgeIcon] || LEVEL_STYLES.bronze

const toNumber = (value) => {
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : 0
}

// Porcentaje hacia el siguiente nivel acotado a 0-100. El cálculo lo hace el
// backend (progress_percent); acá solo se protege la barra de valores raros.
export const clampPercent = (value) => Math.min(100, Math.max(0, toNumber(value)))

// Período del ledger ('2026-09') como texto: "septiembre de 2026". Con Intl y
// no con moment: el locale de moment en español solo queda activo si antes se
// cargó alguna pantalla que lo cambia global.
export const formatPeriod = (period) => {
  const date = period && moment(period, 'YYYY-MM', true)
  return date && date.isValid() ? date.toDate().toLocaleDateString('es-VE', { month: 'long', year: 'numeric' }) : null
}

// Texto de un multiplicador: 1.25 -> "x1,25"; 1.2 como bono -> "+20%".
export const formatMultiplier = (value) => `x${formatNumber(toNumber(value) || 1, 2)}`

export const formatBonusPercent = (multiplier) => `+${formatNumber((toNumber(multiplier) - 1) * 100, 0)}%`

export const formatNumber = (value, maximumFractionDigits = 0) =>
  toNumber(value).toLocaleString('es-VE', { maximumFractionDigits, minimumFractionDigits: 0 })

export const formatDeadline = (date) =>
  moment(date).toDate().toLocaleDateString('es-VE', { weekday: 'long', day: 'numeric', month: 'long' })

// HO-TASK-283: estado del mes en curso para la racha.
//
// La API todavía no expone el estado del período ni su fecha límite (el día
// límite de puntualidad es una regla del backend, PUNCTUALITY_DAY_LIMIT). Se
// pidió agregar a GET /rewards/me un campo opcional:
//   current_period: { period: 'YYYY-MM', deadline_at: ISO 8601,
//                     status: 'pending' | 'in_review' | 'on_time' | 'late' } | null
// Si llega, se usa tal cual. Si no, lo único que se puede afirmar con los datos
// actuales es que el mes ya quedó pagado a tiempo: last_qualified_payment_period
// es el mes en curso. En cualquier otro caso se devuelve null y el widget
// muestra solo la racha, sin cuenta regresiva ni alertas que podrían ser falsas.
export const getCurrentPeriodStatus = (summary, now = moment()) => {
  const fromApi = summary?.current_period
  if (fromApi?.status) {
    return { ...fromApi, label: formatPeriod(fromApi.period) || 'este mes' }
  }

  const currentMonth = now.format('YYYY-MM')
  if (summary?.last_qualified_payment_period === currentMonth) {
    return { period: currentMonth, status: 'on_time', deadline_at: null, label: formatPeriod(currentMonth) }
  }

  return null
}

export const formatUsd = (value) =>
  `$ ${toNumber(value).toLocaleString('es-VE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`

// Origen de cada movimiento del historial, con las fuentes del ledger
// (RewardTransaction::SOURCE_ENUM). El texto principal es `description` del
// backend; esta tabla da el ícono y el respaldo si `description` viene vacío.
export const TRANSACTION_SOURCES = {
  payment: { label: 'Pago de cuota aprobado', icon: faCalendarCheck, color: 'success' },
  punctuality_bonus: { label: 'Bono por pago puntual', icon: faGift, color: 'success' },
  streak_bonus: { label: 'Bono por racha de puntualidad', icon: faFire, color: 'warning' },
  accelerator: { label: 'Acelerador de puntos', icon: faRocket, color: 'info' },
  referral: { label: 'Referido calificado', icon: faUserPlus, color: 'info' },
  survey: { label: 'Encuesta patrocinada', icon: faClipboardList, color: 'info' },
  raffle: { label: 'Sorteo mensual', icon: faTicketAlt, color: 'warning' },
  redemption: { label: 'Canje de Homelan Coins', icon: faArrowDown, color: 'primary' },
  reversal: { label: 'Reverso', icon: faUndo, color: 'danger' },
  marketplace: { label: 'Contratación en el Marketplace', icon: faStore, color: 'primary' }
}

const DEFAULT_SOURCE = { label: 'Movimiento', icon: faCoins, color: 'secondary' }

export const getTransactionSource = (source) => TRANSACTION_SOURCES[source] || DEFAULT_SOURCE

// HO-TASK-286: referidos.
//
// Valores de las reglas de referidos. La API no los expone todavía (son
// RewardRule configurables por root: referral_hc, referral_raffle_tickets y
// referral_monthly_cap), así que se pidió agregar a GET /rewards/referrals:
//   rules: { hc_per_referral, raffle_tickets_per_referral, monthly_cap }
// Mientras no llegue, se usan los valores del RewardRulesSeeder.
const REFERRAL_RULE_DEFAULTS = { hc_per_referral: 1000, raffle_tickets_per_referral: 5, monthly_cap: 3 }

export const getReferralRules = (data) => {
  const rules = { ...REFERRAL_RULE_DEFAULTS, ...(data?.rules || {}) }
  return {
    hc: toNumber(rules.hc_per_referral),
    tickets: toNumber(rules.raffle_tickets_per_referral),
    monthlyCap: toNumber(rules.monthly_cap)
  }
}

// Estados de un referido (RewardReferral::STATUS_ENUM) en palabras del
// propietario. `qualified` sin premiar solo queda así cuando el referidor ya
// llegó al tope mensual de referidos premiados (ReferralService::qualify).
export const REFERRAL_STATUSES = {
  pending: {
    label: 'Esperando su primer pago',
    color: 'warning',
    description: () => 'Se registró con tu código. Ganarás tus Coins cuando su condominio tenga su primer pago de cuota aprobado en Homelan.'
  },
  qualified: {
    label: 'En espera',
    color: 'info',
    description: () => 'Su condominio ya tuvo su primer pago aprobado, pero este mes ya alcanzaste el máximo de referidos premiados.'
  },
  rewarded: {
    label: 'Premiado',
    color: 'success',
    description: (referral) => (referral.rewarded_at ? `Recibiste tus Coins el ${formatDate(referral.rewarded_at)}.` : 'Recibiste tus Coins.')
  },
  rejected: {
    label: 'No válido',
    color: 'secondary',
    description: () => 'Este registro no cumple las condiciones del programa, por ejemplo porque es tu propia cuenta o ya había sido referido antes.'
  }
}

export const getReferralStatus = (status) => REFERRAL_STATUSES[status] || REFERRAL_STATUSES.pending

// Fecha 'YYYY-MM-DD' como "12 sep 2026".
export const formatDate = (date) => {
  const parsed = date && moment(date, 'YYYY-MM-DD')
  return parsed && parsed.isValid()
    ? parsed.toDate().toLocaleDateString('es-VE', { day: 'numeric', month: 'short', year: 'numeric' })
    : ''
}

// "junta.valle@gmail.com" -> "ju•••@gmail.com". La API hoy devuelve el correo
// completo del referido; se enmascara acá para no mostrarlo en pantalla.
export const maskEmail = (email) => {
  if (!email || typeof email !== 'string') return 'Referido sin correo'
  const [local, domain] = email.split('@')
  if (!domain) return `${email.slice(0, 2)}•••`
  const visible = local.length > 3 ? local.slice(0, 2) : local.slice(0, 1)
  return `${visible}•••@${domain}`
}

// Enlace de registro con el código ya cargado (lo lee AuthWizardProvider).
export const buildReferralLink = (code) => {
  const appUrl = (import.meta.env.VITE_URL || window.location.origin).replace(/\/$/, '')
  return `${appUrl}/register?ref=${encodeURIComponent(code)}`
}

export const buildReferralWhatsAppUrl = (code) => {
  const text =
    '¡Hola! Te recomiendo Homelan para administrar tu condominio: cobranza, pagos y cuentas claras en un solo lugar. ' +
    `Regístrate con mi código ${code} aquí: ${buildReferralLink(code)}`
  return `https://wa.me/?text=${encodeURIComponent(text)}`
}

// HO-TASK-288: porcentajes del resumen del condominio. 77.46 -> "77,5 %".
export const formatPercent = (value, maximumFractionDigits = 1) => `${formatNumber(value, maximumFractionDigits)} %`

// Período 'YYYY-MM' abreviado para ejes: "sept 26".
export const formatShortPeriod = (period) => {
  const date = period && moment(period, 'YYYY-MM', true)
  return date && date.isValid()
    ? date.toDate().toLocaleDateString('es-VE', { month: 'short', year: '2-digit' }).replace('.', '')
    : period
}
